// @flow
import React, { Component, Fragment } from 'react';
import { connect } from 'react-redux';
import { DisplayActions, ProfileActions } from 'store/actionCreators';
import type { State } from 'store';
import type { ProductItem } from 'store/modules/display';
import throttle from 'lodash/throttle';
import { getScrollBottom, preventStickBottom } from 'lib/common';
import { type Profile } from 'store/modules/profile';
import UserProductCardList from 'components/user/UserProductCardList';
import { Helmet } from 'react-helmet';

type OwnProps = {
  username: string,
  tag?: string,
};

type Props = OwnProps & {
  products: ?(ProductItem[]),
  prefetched: ?(ProductItem[]),
  hasEnded: boolean,
  prefetching: boolean,
  loading: boolean,
  rawTagName: ?string,
  shouldCancel: boolean,
  profile: ?Profile,
  displayName: string,
  currentUsername: ?string,
  currentTag: ?string,
};

type UserProductsState = {
  loading: boolean,
};

class UserProducts extends Component<Props, UserProductsState> {
  prevCursor: ?string = null;

  state = {
    loading: false,
  };

  initialize = async () => {
    const {
      username,
      tag,
      shouldCancel,
      profile,
      products,
      currentUsername,
      currentTag,
    } = this.props;
    if (
      currentUsername === username && // check username
      products && // check Product length
      products.length > 0 &&
      (tag === currentTag || (!tag && !currentTag)) // tag equals (or both undefined/null)
    ) {
      return;
    }
    if (!profile) return;
    if (!shouldCancel) {
      DisplayActions.clearUserProducts();
      this.setState({ loading: true });
      if (tag) {
        try {
          await ProfileActions.getTagInfo(tag);
        } catch (e) {
          this.setState({ loading: false });
          console.log(e);
        }
      }
      const { rawTagName } = this.props;
      try {
        await DisplayActions.getUserProducts({
          username,
          tag: tag ? rawTagName || undefined : undefined,
        });
        this.setState({ loading: false });
      } catch (e) {
        this.setState({ loading: false });
        console.log(e);
      }
    }
    this.prefetch();
  };

  componentDidMount() {
    this.initialize();
    this.listenScroll();
  }

  componentWillUnmount() {
    this.unlistenScroll();
  }

  componentDidUpdate(prevProps) {
    if (prevProps.tag !== this.props.tag || prevProps.profile !== this.props.profile) {
      this.initialize();
    }
  }

  prefetch = async () => {
    const { prefetched, hasEnded, products, prefetching, loading, username, tag } = this.props;
    if (prefetched) {
      DisplayActions.revealPrefetched('user');
      await Promise.resolve(); // next tick
    }
    if (hasEnded || prefetching || loading || !products || products.length === 0) return;
    const lastId = products[products.length - 1].id;
    if (this.prevCursor === lastId) return;
    this.prevCursor = lastId;
    try {
      await DisplayActions.prefetchUserProducts({
        username,
        tag,
        cursor: lastId,
      });
      preventStickBottom();
    } catch (e) {
      console.log(e);
    }
  };

  onScroll = throttle(() => {
    const scrollBottom = getScrollBottom();
    if (scrollBottom > 1000) return;
    this.prefetch();
  }, 100);

  listenScroll = () => {
    window.addEventListener('scroll', this.onScroll);
  };

  unlistenScroll = () => {
    window.removeEventListener('scroll', this.onScroll);
  };

  render() {
    const {
      products,
      loading,
      prefetching,
      profile,
      rawTagName,
      displayName,
      username,
    } = this.props;

    if (!products) {
      // TODO: Show placeholder
      return <UserProductCardList.Placeholder />;
    }

    return (
      <Fragment>
        {rawTagName ? (
          <Helmet>
            <title>{`${displayName}의 상품 | 캐시바이`}</title>
            <meta
              name="description"
              content={`${displayName}이 등록한 #${rawTagName} 관련 상품 ${products.length}개를 살펴보세요.`}
            />
          </Helmet>
        ) : (
          <Helmet>
            <title>{`${displayName}의 상품 | 캐시바이`}</title>
            <meta name="description" content={`${displayName}이 등록한 관련 상품을 살펴보세요.`} />
          </Helmet>
        )}
        <UserProductCardList products={products} username={username} />
      </Fragment>
    );
  }
}

export default connect(
  ({ display, pender, profile, common }: State) => ({
    products: display.user.products,
    prefetched: display.user.prefetched,
    hasEnded: display.user.end,
    prefetching: pender.pending['display/PREFETCH_USER_PRODUCTS'],
    loading: pender.pending['display/GET_USER_PRODUCTS'] || pender.pending['profile/GET_TAG_INFO'],
    rawTagName: profile.rawTagName,
    shouldCancel: common.ssr && !common.router.altered,
    profile: profile.profile,
    displayName: profile.profile && profile.profile.display_name,
    username: profile.profile && profile.profile.username,
    currentUsername: display.user.currentUsername,
    currentTag: display.user.currentTag,
  }),
  () => ({}),
)(UserProducts);



// WEBPACK FOOTER //
// ./src/containers/user/UserProducts.js